import FormField from '@risk-smart/themed-cloudscape-components/form';
import Input from '@risk-smart/themed-cloudscape-components/input';

import { getFriendlyId, isParentType } from '@/utils/friendlyId';

import { nullDataChartLabel } from '../nullData';
import type { CellInfo, ReportFieldType } from './types';

const getText = ({ fieldData, fieldDef }: CellInfo): null | string => {
  const value = fieldData.value;
  if (fieldDef.displayType !== 'number') {
    throw new Error('number field type used out of context');
  }
  if (value === null) {
    return null;
  }
  // Polymorphic datasets (e.g. issues) resolve the prefix per-row from a parent type carried on
  // the cell's meta, so each sub-type gets its correct friendly-id prefix (RE-, BL-, ...).
  if (fieldDef.prefixSourceMetaKey) {
    const metaType = fieldData.meta?.[fieldDef.prefixSourceMetaKey];
    if (metaType && isParentType(metaType)) {
      const sequentialId = typeof value === 'number' ? value : Number(value);

      return getFriendlyId(metaType, sequentialId);
    }
    // Meta absent (e.g. front end deployed ahead of backend) — fall back to the static prefix.
  }
  if (fieldDef.prefix) {
    return `${fieldDef.prefix}${value}`;
  }

  return value.toString();
};

export const number: ReportFieldType = {
  cell: getText,
  getChartLabel: (cellData) => {
    const label = getText(cellData);

    if (label === null) {
      return nullDataChartLabel();
    }

    return label;
  },
  exportVal: (cellData) => {
    const label = getText(cellData);

    return label ?? '';
  },

  propertyConfig(fieldDef) {
    if (fieldDef.displayType !== 'number') {
      throw new Error('number field type used out of context');
    }

    // Polymorphic fields resolve the prefix per-row, so there is no single static prefix to show
    // in the (row-less) filter UI — the filter operates on the raw numeric value either way.
    const filterPrefix = fieldDef.prefixSourceMetaKey
      ? undefined
      : fieldDef.prefix;

    return {
      key: fieldDef.key,
      groupValuesLabel: fieldDef.groupValuesLabel,
      propertyLabel: fieldDef.propertyLabel,
      operators: ['=', '!=', '<', '<=', '>', '>='].map((operator) => {
        return {
          operator,
          form: (props) => {
            return (
              <FormField>
                <div className={'flex items-center'}>
                  {filterPrefix ?? ''}
                  <Input
                    step={1}
                    value={props.value}
                    type={'number'}
                    onChange={(e) => props.onChange(e.detail.value)}
                  />
                </div>
              </FormField>
            );
          },
          format: (value) => {
            if (filterPrefix) {
              return `${filterPrefix}${value}`;
            }

            return value;
          },
        };
      }),
    };
  },
};
